import { Grid, makeStyles, Typography } from '@material-ui/core';
import { QuickLinkCard, QuickLinkCardColor } from '../home/QuickLinkCard';
import { resolveIcon } from '../../modules/contentPages/widgets';
import {
  boschGray,
} from '../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  groupTitle: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: boschGray[40],
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: theme.spacing(1),
  },
}));

export type LinkGridItem = {
  title: string;
  primaryText: string;
  secondaryText?: string;
  actionLabel?: string;
  actionUrl: string;
  icon?: string;
  color?: QuickLinkCardColor;
  external?: boolean;
};

export type LinkGridProps = {
  title?: string;
  /**
   * 1-4 items per row depending on viewport. Set explicitly via
   * `columns` (clamped to 2/3/4) if you want to force a specific
   * density — useful for short link sets that would otherwise look
   * lonely at 4 columns.
   */
  columns?: 2 | 3 | 4;
  links: LinkGridItem[];
};

/**
 * Grid of `QuickLinkCard`s collected under one optional heading.
 *
 * Why a grid widget on top of the individual `quick-link-card`:
 *   - Many content pages want "a section of related links" (e.g.
 *     "Quick links" or "Useful dashboards") instead of one card at a
 *     time. Building that out of 6 separate `quick-link-card` widgets
 *     means 6 entries in the editor's selected list — tedious to
 *     reorder, hard to scan.
 *   - This widget treats the whole bundle as a single item: one
 *     entry in the editor, one drag handle, one preview. The admin
 *     adds/removes individual links INSIDE the widget's config.
 *
 * Visually identical to `QuickStatsCards` on the home page (same
 * grid, same gradients), but the contents are 100% admin-driven.
 */
export const LinkGrid = ({ title, columns = 4, links }: LinkGridProps) => {
  const classes = useStyles();
  // Clamp to a sensible range so the dropdown can't produce
  // single-column or 12-column grids that look broken.
  const safeCols = Math.min(4, Math.max(2, columns)) as 2 | 3 | 4;
  const md = (12 / safeCols) as 3 | 4 | 6;
  return (
    <div>
      {title && (
        <Typography className={classes.groupTitle}>{title}</Typography>
      )}
      <Grid container spacing={2}>
        {links.map((link, i) => (
          <Grid
            key={`${link.title}-${i}`}
            item
            xs={12}
            sm={6}
            md={md}
          >
            <QuickLinkCard
              title={link.title}
              primaryText={link.primaryText}
              secondaryText={link.secondaryText ?? ''}
              actionLabel={link.actionLabel ?? 'Open'}
              actionUrl={link.actionUrl}
              icon={resolveIcon(link.icon)}
              color={link.color ?? 'blue'}
              external={link.external ?? false}
            />
          </Grid>
        ))}
      </Grid>
    </div>
  );
};
